import { Container, Service } from 'typedi';
import { AppState } from '../app-state';
import { SessionStorageToken, UCFData, WindowToken } from '../../injection-tokens';
import {
    EventFormatterBuilder,
    EventFormatterBuilderFactory,
} from '@888unifiedclient/event-formatter';
import {
    FlowComponents,
    IdsOfElements,
    SPINNER_PROMISE_TIMEOUT,
    StaticPaths,
    ViewSource,
} from '../../models/enums/general-enums';
import { LoggerProvider } from '../logger';
import {
    AppIdentifiers,
    OpeningMethod,
    AppStateEnum,
    AreaType,
    ActionName,
    TriggerType,
    IRemoveAppContainerDTO,
    IPerformActionTopicPayload,
    IUiChannel,
    IViewInjectorChannel,
    MessageBroker,
    ICreateAppContainerData,
    IShowErrorDTO,
    IToggleAppContainerDTO,
    ILogger,
    PostLoginActionNameEnum,
    ActionID,
    ISessionChannel,
} from '@sparkware/uc-sdk-core';
import { NavigationResultCode, NavigationResultDesciption } from '../navigation/enums/consts';
import { PerformanceMarks, PerformanceMeasures } from '../performance/enums/consts';
import { B2CIntegration } from '../b2c-integration';
import { ClickStreamTrackingProvider } from '../tracking';
import ContentApi from '../../../APIs/ContentApi';
import { IUrlHandlerOptions } from '../router/models/IUrlHandlerOptions';
import IViewInjectorClickStreamEventData from './models/IViewInjectorClickStreamEventData';
import { IContentAndTitleResult } from './models/IContentAndTitleResult';
import { NotFound } from '../../../Modules/Utils/API/Constants/ApiConstants';
import { PerformanceManager } from '../performance/performance-manager';
import { ClientProvider } from '../../models/enums/general-enums';
import { UserContextToken } from '../../injection-tokens';
import { IUserContext } from '../user-context/user-context-interface';
import { Utils } from '../utils';
import { v4 as uuidv4 } from 'uuid';
import { UserareaService } from '../external/userarea';
import { BrandUtils } from '../../../Modules/Utils/BrandUtils';
import { PageContentManager } from './managers/page-content.manager';
import { UrlUtils } from '../utils/urlUtils';
import { RouterUtils } from '../router/router.utils';
import { IClientIntegrationFacade } from '../client-integration/interfaces/IClientIntegrationFacade';
import { ICMSContentLoader } from './models/ICMSContentLoader';
import { IBIEventHandler } from '../bi/models/IBIEventHandler';
import { SiteSegmentationContext } from '../site-segmentation-context';
import { IAppCatalog } from '../app-catalog/interfaces';
import { LoaderManager } from '../../loaders/LoaderManager';
import { IActionHandlerFactory } from '../action-handler/models/IActionHandlerFactory';
import { getAllowAttr } from '@888webteam/iframe-allow-camera';
import { PostLoginStorage } from '../post-login/post-login.storage';
import { StorageItemEnum } from '../../models/enums/storage-enums';
import { IPostLogin } from '../post-login/models/IPostLogin';
import { LocalSimpleStoreService } from '../storage/implementations/simple-store';

enum EventType {
    ClientPage = 'Navigate to client ready',
    ContentPage = 'Navigate to Content ready',
}

@Service()
export class ViewInjector {
    private _ucContainer: HTMLDivElement;
    public ActiveView: ViewSource;
    private _userContext: IUserContext;
    private _eventFormatterBuilder: EventFormatterBuilder;
    private _pageContentManager: PageContentManager;
    private _lastApplicationLayerLevel = 0;
    private componentId: number;
    private publicationId: number;
    private templateId: number;

    private readonly _window: Window;
    private readonly _logger: ILogger;
    private readonly _performanceManager: PerformanceManager;
    private readonly _utils: Utils;
    private readonly _appState: AppState;
    private readonly _userAreaService: UserareaService;
    private readonly _urlUtils: UrlUtils;
    private readonly _routerUtils: RouterUtils;
    private readonly _siteSegmentationContext: SiteSegmentationContext;
    private readonly _trackingProvider: ClickStreamTrackingProvider;
    private readonly _b2cIntegration: B2CIntegration;
    private readonly _ucfData: any;
    private readonly _viewInjectorChannel: IViewInjectorChannel;
    private readonly _uiChannel: IUiChannel;
    private readonly _sessionStorage: Storage;
    private readonly _postLoginStorage: PostLoginStorage;
    private readonly _sessionManagerChannel: ISessionChannel;
    private readonly _localSimpleStoreService: LocalSimpleStoreService;

    private get _appCatalogPromise(): Promise<IAppCatalog> {
        return LoaderManager.Instance.AppCatalogLoader.Instance;
    }

    private get _biEventHandlerPromise(): Promise<IBIEventHandler> {
        return LoaderManager.Instance.BIEventHandlerLoader.Instance;
    }

    private get _cmsContentLoaderPromise(): Promise<ICMSContentLoader> {
        return LoaderManager.Instance.CMSContentLoaderLoader.Instance;
    }

    private get _actionHandlerFactoryPromise(): Promise<IActionHandlerFactory> {
        return LoaderManager.Instance.ActionHandlerFactoryLoader.Instance;
    }

    private get _postLoginPromise(): Promise<IPostLogin> {
        return LoaderManager.Instance.PostLoginLoader.Instance;
    }

    private get _clientIntegrationFacadePromise(): Promise<IClientIntegrationFacade> {
        return LoaderManager.Instance.ClientIntegrationFacadeLoader.Instance;
    }

    constructor() {
        this._window = Container.get(WindowToken);
        this._logger = Container.get(LoggerProvider).getLogger('ViewInjector');
        this._utils = Container.get(Utils);
        this._urlUtils = Container.get(UrlUtils);
        this._userContext = Container.get(UserContextToken);
        this._appState = Container.get(AppState);
        this._performanceManager = Container.get(PerformanceManager);
        this._userAreaService = Container.get(UserareaService);
        this._pageContentManager = Container.get(PageContentManager);
        this._routerUtils = Container.get(RouterUtils);
        this._siteSegmentationContext = Container.get(SiteSegmentationContext);
        const eventFormatterBuilderFactory = Container.get(EventFormatterBuilderFactory);
        this._trackingProvider = Container.get(ClickStreamTrackingProvider);
        this._b2cIntegration = Container.get(B2CIntegration);
        this._ucfData = Container.get(UCFData);
        this._sessionStorage = Container.get(SessionStorageToken);
        this._postLoginStorage = Container.get(PostLoginStorage);
        this._localSimpleStoreService = Container.get(LocalSimpleStoreService);
        this._eventFormatterBuilder =
            eventFormatterBuilderFactory.createEventFormatterBuilder('ViewInjector');
        this._viewInjectorChannel = MessageBroker.getInstance().viewInjector;
        this._uiChannel = MessageBroker.getInstance().ui;
        this._sessionManagerChannel = MessageBroker.getInstance().session;

        void this._start();
        this.ShowClient = this.ShowClient.bind(this);
        this.ShowContent = this.ShowContent.bind(this);
        this._switchActiveViewOnShowClient = this._switchActiveViewOnShowClient.bind(this);
        this._switchActiveViewOnShowContent = this._switchActiveViewOnShowContent.bind(this);
    }

    public async ShowClient(
        uri: string,
        options: IUrlHandlerOptions = { clientNavigated: false },
    ): Promise<void> {
        const cmsContentLoader = await this._cmsContentLoaderPromise;
        cmsContentLoader.Disconnect();
        this._toggleCMSContentVisibility(false);
        this._utils.ClearElementWithId(IdsOfElements.UcFullContent);
        this._logger.debug(`ShowClient is being called with uri ${uri}`);
        const markStart = `${PerformanceMarks.ViewInjectorShowClientStart}-${uri}`,
            measureName = `${PerformanceMeasures.ViewInjectorShowClient}-${uri}`;
        this._performanceManager.startWatch(markStart);
        this._observeClientMode();
        this._utils.ShowElementWithId(IdsOfElements.UcSpectateSportClientView);
        let visiblePublished = false;
        if (this.ActiveView === ViewSource.Content) {
            this._toggleSpinner(true, 'showClient');
            visiblePublished = true;
        }
        try {
            await this._utils.PromiseTimeout(
                SPINNER_PROMISE_TIMEOUT,
                this._switchActiveViewOnShowClient(uri, options, () => {
                    this._toggleCMSContentVisibility(true);
                }),
            );
        } catch (err) {
            this._logger.warn(`ShowClient: error:`, err);
        } finally {
            if (visiblePublished) {
                this._toggleSpinner(false, 'showClient');
            }
            const duration = this._performanceManager.getDuration(measureName, markStart);
            this._performanceManager.clearWatches([markStart]);
            this._sendPerformanceNotification(duration, EventType.ClientPage);
        }
    }

    public async ShowContent(
        uri: string,
        options: IUrlHandlerOptions = { clientNavigated: false },
    ): Promise<void> {
        const cmsContentLoader = await this._cmsContentLoaderPromise;
        cmsContentLoader.Disconnect();
        this._toggleCMSContentVisibility(false);
        this._utils.ClearElementWithId(IdsOfElements.UcFullContent);
        this._logger.debug(`ShowContent is being called with uri ${uri}`);
        const markStart = `${PerformanceMarks.ViewInjectorShowContentStart}-${uri}`,
            measureName = `${PerformanceMeasures.ViewInjectorShowContent}-${uri}`;
        this._performanceManager.startWatch(markStart);
        await this._toggleSpinner(true, 'showContent');
        try {
            await this._utils.PromiseTimeout(
                SPINNER_PROMISE_TIMEOUT,
                this._switchActiveViewOnShowContent(uri),
            );
            await this._toggleSpinner(false, 'showContent');
            this._toggleCMSContentVisibility(true);
        } catch (err) {
            this._logger.warn(`ShowContent: error:`, err);
        } finally {
            const duration = this._performanceManager.getDuration(measureName, markStart);
            this._performanceManager.clearWatches([markStart]);
            this._sendPerformanceNotification(duration, EventType.ContentPage);
            const rootUri = this._urlUtils.getNoLangURL(uri);
            if (
                BrandUtils.isPokerClient() &&
                rootUri === StaticPaths.PokerRoot &&
                !options.clientNavigated
            ) {
                void this._clientIntegrationFacadePromise.then((facade) =>
                    facade.navigatePublish(rootUri),
                );
            }
        }
    }

    public HideClientAndContent = (withFooter: boolean = true) => {
        if (this.ActiveView === ViewSource.Sport) {
            void this._clientIntegrationFacadePromise.then((facade) => facade.deactivatePublish());
        }
        this._utils.HideElementWithId(IdsOfElements.UcSpectateSportClientView);
        this._utils.HideElementWithId(IdsOfElements.UcFullContent);
        if (withFooter) {
            this._utils.HideElementWithId(IdsOfElements.UcFooter);
        }
    };

    public ShowClientAndContent = (isClientNavigation: boolean = true) => {
        if (isClientNavigation) {
            this._utils.ShowElementWithId(IdsOfElements.UcSpectateSportClientView);
        }
        this._utils.ShowElementWithId(IdsOfElements.UcFullContent);
        this._utils.ShowElementWithId(IdsOfElements.UcFooter);
    };

    public InjectClient = () => {
        this._logger.debug('Injecting spectate content to sport client...');
        let spectateSportClientView = this._window.document.getElementById(
            IdsOfElements.SpectateSportClientView,
        );
        let spectateSportClientDiv = this._window.document.getElementById(
            IdsOfElements.UcSpectateSportClientView,
        );
        this._utils.MergeElements(
            spectateSportClientView,
            spectateSportClientDiv,
            IdsOfElements.SpectateSportClientView,
            IdsOfElements.UcSpectateSportClientView,
            'none',
        );
    };

    public async OnAuthenticationStatusChangeContentUpdate() {
        const cmsContentLoader = await this._cmsContentLoaderPromise;

        if (this.ActiveView === ViewSource.FirstHit) {
            this._logger.debug(
                'UpdateContentAndFooter: No need to update content and footer on first hit',
            );
            return Promise.resolve();
        }
        if (this.ActiveView === ViewSource.Content) {
            this._toggleSpinner(true, 'authenticationChange');
        }
        this._toggleCMSContentVisibility(false);
        this._updateContentAndFooter()
            .then(() => {
                cmsContentLoader.ResolveWhenReady(this.ActiveView).then(() => {
                    this._toggleCMSContentVisibility(true);
                    if (this.ActiveView === ViewSource.Content) {
                        this._toggleSpinner(false, 'authenticationChange');
                    }
                });
            })
            .catch((err) => {
                this._logger.warn(`OnAuthenticationStatusChangeContentUpdate:`, err);
            });
    }

    public CreateAppContainer(data: ICreateAppContainerData): void {
        try {
            this._logger.debug('CreateAppContainer start');
            const currentAppState = this._appState.getAppState(data.appID)?.state;
            this.updateLastApplicationLayerLevel(true);
            this._appState.setAppState(
                data.appID,
                currentAppState,
                this._lastApplicationLayerLevel,
            );
            const createAppContainerData = {
                ...data,
                applicationLayerLevel: this._lastApplicationLayerLevel,
            };
            this._uiChannel.topics.CreateAppContainer.publish(
                { publisher: FlowComponents.ViewInjector },
                createAppContainerData,
            );
        } catch (err) {
            this._logger.error('CreateAppContainer failed', err);
            throw err;
        }
    }

    public ToggleAppContainer(data: IToggleAppContainerDTO) {
        try {
            const { appID, appState } = data;
            this._appState.setAppState(appID, appState);
            this._logger.debug('ToggleAppContainer start');
            this._uiChannel.topics.toggleAppContainer.publish(
                { publisher: FlowComponents.ViewInjector },
                data,
            );
        } catch (err) {
            this._logger.error('ToggleAppContainer failed', err);
        }
    }

    public RemoveAppContainer(data: IRemoveAppContainerDTO) {
        try {
            this._logger.debug('RemoveAppContainer start');

            if (data.canClose) {
                this.updateLastApplicationLayerLevel(false);

                this._appState.setAppState(
                    data.appID,
                    AppStateEnum.Closed,
                    this._lastApplicationLayerLevel,
                );
            }

            const removeAppContainerData = {
                ...data,
                applicationLayerLevel: this._lastApplicationLayerLevel,
            };
            this._uiChannel.topics.RemoveAppContainer.publish(
                { publisher: FlowComponents.ViewInjector },
                removeAppContainerData,
            );
        } catch (err) {
            this._logger.error('RemoveAppContainer failed', err);
            throw err;
        }
    }

    public async ShowApp(appID: string) {
        if (!appID) return;
        const appCatalog = await this._appCatalogPromise;
        const appCatalogData = await appCatalog.getAppCatalogData(appID);
        if (appCatalogData?.app.openingMethod !== OpeningMethod.GNP) {
            this._appState.setAppState(appID, AppStateEnum.Active);
        }
    }

    public async ShowError(data: IShowErrorDTO) {
        this._appState.setAppState(data.appID, AppStateEnum.Error);
        this._uiChannel.topics.ShowAppError.publish(
            { publisher: FlowComponents.ViewInjector },
            data,
        );
    }

    public async ShowMainAppView(actionID: ActionName, pageTitle: string) {
        const cmsContentLoader = await this._cmsContentLoaderPromise;
        cmsContentLoader.Disconnect();
        if (!!pageTitle) {
            this._window.document.title = pageTitle;
        }
        const data: IPerformActionTopicPayload = {
            actionID: actionID,
            correlationID: uuidv4(),
            launchInfo: {
                businessCorrelationID: uuidv4(),
                sequentialCorrelationID: 1,
                channel: {
                    area: AreaType.button,
                    element: 'Default',
                    source: 'Open Poker History',
                },
                appSpecificParameters: {},
                trigger: TriggerType.userSelection,
                containerID: undefined,
                sourceAppID: AppIdentifiers.UnifiedClient,
                sourceAppVersion: '0.0.0',
                clientVersion: this._window.pageContextManager.getDeviceData().clientVersion,
            },
            actionData: {},
        };
        const actionHandlerFactory = await this._actionHandlerFactoryPromise;
        actionHandlerFactory.performAction(data);
        this.HideClientAndContent(false);
        this.ActiveView = ViewSource.Content;
        this._toggleCMSContentVisibility(false);
        this._utils.ShowElementWithId(IdsOfElements.UcFooter);

        this._injectFooter().finally(() => {
            cmsContentLoader.ResolveWhenReady(ViewSource.Content).then(() => {
                this._toggleCMSContentVisibility(true);
            });
        });
    }

    public SetActiveView = (nextActiveView: ViewSource) => {
        this._logger.debug(`_setActiveView: Active view was set to: ${nextActiveView}`);
        this.ActiveView = nextActiveView;
    };

    public ResetView = () => {
        if (this.ActiveView !== ViewSource.FirstHit) {
            try {
                this._pageContentManager.reset();
            } catch (err) {
                this._logger.warn(`ResetView:`, err);
            }
            this._updateContentSync('');
        }
    };

    public OnClientInitSuccess = () => {
        this._toggleSpinner(false, 'clientInitSuccess');
    };

    public CreateForm(
        path: string,
        params: Record<string, { key: string; value: string }>,
        method?: string,
        target?: string,
        windowReference?: Window,
    ): void {
        const defaultTarget = '_blank';
        const formMethod = method || 'post';
        const formTarget = target || defaultTarget;

        const form = this._window.document.createElement('form');
        form.setAttribute('method', formMethod);
        form.setAttribute('action', path);
        form.setAttribute('target', formTarget);

        for (const key in params) {
            if (params.hasOwnProperty(key)) {
                const hiddenField = this._window.document.createElement('input');
                hiddenField.setAttribute('type', 'hidden');
                hiddenField.setAttribute('name', params[key].key);
                hiddenField.setAttribute('value', params[key].value);
                form.appendChild(hiddenField);
            }
        }

        if (windowReference) {
            windowReference.document.body.appendChild(form);
            windowReference.document.forms[0].submit();
            windowReference.document.body.removeChild(form);
        } else {
            this._window.document.body.appendChild(form);

            if (defaultTarget === formTarget) {
                setTimeout(() => {
                    form.submit();
                    this._window.document.body.removeChild(form);
                }, 0);
            } else {
                form.submit();
                this._window.document.body.removeChild(form);
            }
        }
    }

    public OpenMobileContainerDiv(
        src: string,
        showCloseButton?: boolean,
        onCloseCallback?: () => void,
    ): void {
        this._logger.debug('OpenMobileContainerDiv:', src);

        let mobileOverlay = this._window.document.getElementById('mobileOverlay');
        if (mobileOverlay) {
            mobileOverlay.parentNode?.removeChild(mobileOverlay);
        }

        mobileOverlay = this._window.document.createElement('div');
        mobileOverlay.setAttribute('id', 'mobileOverlay');
        mobileOverlay.setAttribute('class', 'mobileOverlay');
        mobileOverlay.setAttribute('data-ignore-click-outside', '');
        mobileOverlay.style.height = `${this._window.innerHeight}px`;

        if (showCloseButton) {
            const closeButtonContainerDiv = this._window.document.createElement('div');
            closeButtonContainerDiv.setAttribute('class', 'mobile-container-close-button-circle');

            const closeButtonDiv = this._window.document.createElement('div');
            closeButtonDiv.setAttribute('id', 'closeButtonDiv');
            closeButtonDiv.setAttribute('class', 'mobile-close-button');

            const closeButtonClickableAreaDiv = this._window.document.createElement('div');
            closeButtonClickableAreaDiv.setAttribute('id', 'closeButtonClickableAreaDiv');
            closeButtonClickableAreaDiv.setAttribute('class', 'mobile-close-button-clickable-area');

            const onCloseFunction = () => {
                this.CloseMobileContainer();
                if (typeof onCloseCallback === 'function') {
                    onCloseCallback();
                }
            };

            closeButtonClickableAreaDiv.addEventListener('click', onCloseFunction, true);

            mobileOverlay.appendChild(closeButtonContainerDiv);
            mobileOverlay.appendChild(closeButtonDiv);
            mobileOverlay.appendChild(closeButtonClickableAreaDiv);
        }

        const iframe = this._window.document.createElement('iframe');
        iframe.setAttribute('id', 'generalIframe');
        iframe.setAttribute('name', 'generalIframe');
        iframe.setAttribute('class', 'mobile-container');

        const cameraAttr = getAllowAttr();
        if (cameraAttr) {
            iframe.setAttribute('allow', cameraAttr);
        }
        iframe.setAttribute('allowFullScreen', 'true');

        mobileOverlay.appendChild(iframe);
        this._window.document.body.appendChild(mobileOverlay);

        if (src) {
            iframe.setAttribute('src', src);
        }

        const ucContainer = this._window.document.getElementById(IdsOfElements.UcContainer);
        if (ucContainer) {
            ucContainer.classList.add('hidden');
        }

        this._window.document.body.style.setProperty('padding-top', '0px');
        this._utils.ScrollToTop();
    }

    public InjectWrappingDivWithCloseButton(
        src: string,
        width: number,
        height: number,
        border: boolean,
        onClose?: () => void,
        productPackageId?: number,
        classToAdd?: string,
    ): void {
        this._logger.debug('InjectWrappingDivWithCloseButton:', src);

        let wrapDivExist = false;
        let wrapDiv = this._window.document.getElementById('WrapDiv');
        const omgIframe = this._window.document.getElementById(IdsOfElements.OmgGeneralIframe);

        if (wrapDiv) {
            if (omgIframe) {
                wrapDivExist = true;
                wrapDiv.style.zIndex = '5003';
                omgIframe.style.display = 'none';
            } else {
                wrapDiv.remove();
            }
        }

        if (!wrapDiv) {
            wrapDiv = this._window.document.createElement('div');
        }

        wrapDiv.setAttribute('id', 'WrapDiv');
        wrapDiv.setAttribute('class', 'lBpopup');
        wrapDiv.setAttribute('data-ignore-click-outside', '');

        const closeButtonWrapDiv = this._window.document.createElement('div');
        closeButtonWrapDiv.setAttribute('id', 'closeButtonWrapDiv');
        closeButtonWrapDiv.setAttribute('class', 'closeButtonWrapDiv');
        wrapDiv.appendChild(closeButtonWrapDiv);

        const closeButtonDiv = this._window.document.createElement('div');
        closeButtonDiv.setAttribute('class', 'rev_closeBtn');
        closeButtonDiv.setAttribute('automation-id', 'rev_closeBtn');
        closeButtonDiv.addEventListener('click', () => this.CloseWrapDiv());

        closeButtonWrapDiv.appendChild(closeButtonDiv);
        closeButtonWrapDiv.style.width = `${width}px`;
        closeButtonWrapDiv.style.height = `${height}px`;

        const ifrm = this._window.document.createElement('iframe');
        const iframeId = productPackageId === 11 ? 'cashierGeneralIframe' : 'generalIframe';
        ifrm.setAttribute('id', iframeId);
        ifrm.setAttribute('name', 'generalIframe');
        ifrm.setAttribute('class', 'lb-join');

        const cameraAttr = getAllowAttr();
        if (cameraAttr) {
            ifrm.setAttribute('allow', cameraAttr);
        }
        ifrm.setAttribute('allowFullScreen', 'true');
        ifrm.scrolling = 'no';

        if (!border) {
            ifrm.classList.add('noBorder');
        }

        ifrm.style.width = `${width}px`;
        ifrm.style.height = `${height}px`;

        if (classToAdd) {
            ifrm.classList.add(classToAdd);
        }

        closeButtonWrapDiv.appendChild(ifrm);

        if (!wrapDivExist) {
            this._window.document.body.appendChild(wrapDiv);
        }

        let overlayDiv = this._window.document.getElementById('overlay');
        if (!overlayDiv) {
            overlayDiv = this._window.document.createElement('div');
            overlayDiv.setAttribute('id', 'overlay');
            overlayDiv.setAttribute('class', 'overlay');
            overlayDiv.setAttribute('data-ignore-click-outside', '');

            this._window.document.body.appendChild(overlayDiv);
            wrapDiv.parentNode?.insertBefore(overlayDiv, wrapDiv);
        }

        if (src) {
            ifrm.setAttribute('src', src);
        }

        if (onClose) {
            closeButtonDiv.addEventListener('click', onClose);
        }

        this._utils.ScrollToTop();
    }

    public InjectWrappingDiv(
        src: string,
        width: number,
        height: number,
        border: boolean,
        isOmgMsg?: boolean,
        classToAdd?: string,
    ): void {
        this._logger.debug('InjectWrappingDiv:', src);

        const iframeName = isOmgMsg ? IdsOfElements.OmgGeneralIframe : 'generalIframe';
        const overlayDivName = 'overlay';
        let wrapDivExist = false;

        let wrapDiv = this._window.document.getElementById('WrapDiv');
        if (wrapDiv) {
            if (isOmgMsg) {
                wrapDivExist = true;
                wrapDiv.style.zIndex = '5003';
            } else {
                wrapDiv.remove();
            }
        }

        if (!wrapDiv) {
            wrapDiv = this._window.document.createElement('div');
            if (isOmgMsg) {
                wrapDiv.style.zIndex = '4999';
            }
        }

        wrapDiv.setAttribute('id', 'WrapDiv');
        wrapDiv.setAttribute('class', 'lBpopup');
        wrapDiv.setAttribute('data-ignore-click-outside', '');

        const ifrm = this._window.document.createElement('iframe');
        ifrm.setAttribute('id', iframeName);
        ifrm.setAttribute('name', iframeName);
        ifrm.setAttribute('class', 'lb-join');

        const cameraAttr = getAllowAttr();
        if (cameraAttr) {
            ifrm.setAttribute('allow', cameraAttr);
        }
        ifrm.setAttribute('allowFullScreen', 'true');

        if (classToAdd) {
            ifrm.classList.add(classToAdd);
        }

        ifrm.scrolling = 'no';
        const deviceData = this._window.pageContextManager.getDeviceData();

        if (deviceData.isMobile && isOmgMsg) {
            ifrm.style.border = 'none';
        }

        if (!border) {
            ifrm.classList.add('noBorder');
        }

        ifrm.style.width = `${width}px`;
        ifrm.style.height = `${height}px`;

        wrapDiv.appendChild(ifrm);
        if (!wrapDivExist) {
            this._window.document.body.appendChild(wrapDiv);
        }

        let overlayDiv = this._window.document.getElementById('overlay');
        if (!overlayDiv) {
            overlayDiv = this._window.document.createElement('div');
            overlayDiv.setAttribute('id', overlayDivName);
            overlayDiv.setAttribute('class', overlayDivName);
            overlayDiv.setAttribute('data-ignore-click-outside', '');
            if (iframeName === IdsOfElements.OmgGeneralIframe) {
                overlayDiv.style.display = 'none';
            }
            this._window.document.body.appendChild(overlayDiv);
            wrapDiv.parentNode?.insertBefore(overlayDiv, wrapDiv);
        }

        if (isOmgMsg) {
            overlayDiv.style.zIndex = wrapDivExist ? '5000' : '4998';
        }

        if (src) {
            ifrm.setAttribute('src', src);
        }

        if (deviceData.isMobile && !isOmgMsg) {
            const ucContainer = this._window.document.getElementById(IdsOfElements.UcContainer);
            if (ucContainer) {
                ucContainer.style.setProperty('position', 'fixed');
            }
        }

        this._utils.ScrollToTop();
    }

    public CloseWrapDiv(obj?: { target?: HTMLElement; srcElement?: HTMLElement }): Promise<void> {
        return new Promise(async (resolve) => {
            let asyncRemove = false;
            const target = obj?.target || obj?.srcElement;
            this._sessionStorage.removeItem('isOmgMsg');
            const wrapDiv = this._window.document.getElementById('WrapDiv');
            const captchaWrapDiv = this._window.document.getElementById('CaptchaWrapDiv');
            const uploadDocumentsPopupDiv =
                this._window.document.getElementById('uploadDocumentsPopupDiv');
            const lowfundsPopupDiv = this._window.document.getElementById('lowfundsPopupDiv');
            const cashierIframe = this._window.document.getElementById('cashierGeneralIframe');
            let completePostLoginAction: PostLoginActionNameEnum = null;
            if (uploadDocumentsPopupDiv) {
                completePostLoginAction = PostLoginActionNameEnum.UploadDocuments;
            } else if (cashierIframe) {
                const currentAction = this._postLoginStorage.getCurrentPostLoginAction();
                if (currentAction?.Name === PostLoginActionNameEnum.UploadDocuments) {
                    completePostLoginAction = currentAction.Name;
                }
            }
            const scrollContainer = this._window.document.getElementById(
                IdsOfElements.UcContentContainer,
            );
            if (scrollContainer) {
                scrollContainer.classList.remove('noScroll');
            }
            const ucContainer = this._window.document.getElementById(IdsOfElements.UcContainer);
            if (ucContainer) {
                ucContainer.removeAttribute('style');
            }
            if (captchaWrapDiv) {
                captchaWrapDiv.parentNode?.removeChild(captchaWrapDiv);
                const captchaOverlay = this._window.document.getElementById('CaptchaOverlay');
                captchaOverlay?.remove();
            } else {
                const omgIframe = this._window.document.getElementById(
                    IdsOfElements.OmgGeneralIframe,
                );
                let removeOverlay = true;
                if (wrapDiv) {
                    if (wrapDiv?.parentNode) {
                        if (target?.parentElement && omgIframe) {
                            // Show the OMG after closing current Iframe
                            removeOverlay = false;
                            wrapDiv.removeChild(target.parentElement);
                            wrapDiv.style.zIndex = '4999';
                            omgIframe.style.display = 'block';
                            const overlayDiv = this._window.document.getElementById('overlay');
                            if (overlayDiv) {
                                overlayDiv.style.zIndex = '4998';
                            }
                        } else if (wrapDiv.contains(omgIframe)) {
                            omgIframe.style.display = 'none';
                            asyncRemove = true;
                            setTimeout(() => {
                                wrapDiv.remove();
                                resolve();
                            }, 700);
                        } else {
                            wrapDiv.remove();
                        }
                    }
                } else {
                    this.CloseMobileContainer();
                }
                const loginPopupDkDiv = this._window.document.getElementById('loginPopupDk');
                if (loginPopupDkDiv?.parentNode) {
                    (loginPopupDkDiv.parentNode as any).remove();
                }
                const overlayDiv = this._window.document.getElementById('overlay');
                if (overlayDiv && removeOverlay) {
                    overlayDiv.remove();
                    const actionStarted = this._localSimpleStoreService.get(
                        StorageItemEnum.ActionStarted,
                    );
                    if (actionStarted) {
                        if (
                            actionStarted === ActionID.openCashier ||
                            actionStarted === ActionID.register
                        ) {
                            this._sessionManagerChannel.topics.actionCompleted.publish(
                                {
                                    publisher: FlowComponents.ViewInjector,
                                },
                                {},
                            );
                        } else {
                            this._sessionManagerChannel.topics.actionCancelled.publish(
                                {
                                    publisher: FlowComponents.ViewInjector,
                                },
                                {},
                            );
                        }
                    }
                }
            }
            if (completePostLoginAction) {
                (await this._postLoginPromise)?.completeAction(completePostLoginAction);
            }
            if (!asyncRemove) {
                resolve();
            }
        });
    }

    public handleContentApiResponse(response: { location?: string; content?: any }): void {
        try {
            const location: string | undefined = response?.location;
            if (location) {
                const locationUrl = this._urlUtils.getParsedUrl(location);
                const isSameSite =
                    !locationUrl.hostname ||
                    locationUrl.hostname === this._window.location.hostname;
                const hasContent =
                    typeof response?.content !== 'undefined' && response?.content !== null;

                if (!isSameSite || !hasContent) {
                    // Different site OR no content returned while location provided => full refresh
                    this._logger.debug(
                        `GetPageContent returned location to another site or without content. Redirecting to: ${locationUrl.href}`,
                    );
                    this._urlUtils.redirectToUrl(locationUrl.href);
                } else {
                    // Same site and we have content => keep SPA, update the address bar
                    const relative = this._urlUtils.getURLWithoutHost(locationUrl.href);
                    const langAware = this._urlUtils.getLangURL(relative);
                    this._logger.debug(
                        `GetPageContent returned location on same site. Updating address bar to: ${langAware}`,
                    );
                    this._urlUtils.historyReplaceState(this._window.document.title, langAware);
                }
            }
        } catch (e) {
            this._logger.warn('ViewInjector', 'Failed handling GetPageContent location', e);
        }
    }

    private CloseMobileContainer(): void {
        const mobileOverlay = this._window.document.getElementById('mobileOverlay');
        if (mobileOverlay) {
            mobileOverlay.parentNode?.removeChild(mobileOverlay);
        }

        const ucContainer = this._window.document.getElementById(IdsOfElements.UcContainer);
        if (ucContainer) {
            ucContainer.classList.remove('hidden');
        }

        this._window.document.body.style.removeProperty('padding-top');
        this._window.document.body.style.removeProperty('position');
    }

    private async _start(): Promise<void> {
        const isNativeSdk = this._utils.isNativeSDK();
        if (!isNativeSdk) {
            const { publicationId, templateId, spectateComponentId, componentId } =
                this._window.pageContextManager.getInfraData();
            const clientProvider = this._utils.getClientProvider();
            this.publicationId = publicationId;
            this.templateId = templateId;
            this.componentId =
                clientProvider !== ClientProvider.Kambi && !!spectateComponentId
                    ? spectateComponentId
                    : componentId;
            this._ucContainer = this._createContainer();
            this._createEmptyComponents();
            this._setFooterContent();
            this.SetActiveView(ViewSource.FirstHit);
        }
    }

    private updateLastApplicationLayerLevel(isIncreased): void {
        try {
            this._logger.debug('[updateLastApplicationLayerLevel] start');
            const change = isIncreased ? 1 : -1;
            const newApplicationLayerLevel = this._lastApplicationLayerLevel + change;
            if (newApplicationLayerLevel >= 0) {
                this._lastApplicationLayerLevel = newApplicationLayerLevel;
                this._logger.debug(
                    `[updateLastApplicationLayerLevel] new-level: ${newApplicationLayerLevel}`,
                );
            } else {
                this._lastApplicationLayerLevel = 0;
                this._logger.debug(`[updateLastApplicationLayerLevel] error, reset to 1`);
            }
        } catch (err) {
            this._logger.error('updateLastApplicationLayerLevel failed', err);
            throw err;
        }
    }

    private _toggleSpinner = async (visible: boolean, component: string) => {
        this._logger.debug(`_toggleSpinner: ${component}: visible: ${visible}`);
        await this._userAreaService.executeOnload(() => {
            this._viewInjectorChannel.topics.SetVisible.publish(
                {
                    publisher: `${FlowComponents.ViewInjector}-${component}`,
                },
                { visible },
            );
        });
    };

    private _injectHiddenContent = () => {
        if (this._urlUtils.isClientMode()) return;
        this._logger.debug('Injecting hidden content to content...');
        let casinoHiddenDiv = this._window.document.getElementById(IdsOfElements.CasinoContent);
        let hiddenDiv = this._window.document.getElementById(IdsOfElements.HiddenContent);
        let contentDiv = this._window.document.getElementById(IdsOfElements.UcFullContent);
        this._utils.MergeElements(
            casinoHiddenDiv ?? hiddenDiv,
            contentDiv,
            IdsOfElements.HiddenContent,
            IdsOfElements.UcFullContent,
            'block',
        );
    };

    private _createContainer(): HTMLDivElement {
        const containerDiv = this._window.document.createElement('div');
        containerDiv.setAttribute('id', IdsOfElements.UcContainer);
        containerDiv.classList.add('transparent-el');
        this._window.document.body.prepend(containerDiv);
        return containerDiv;
    }

    private _sendPerformanceNotification(duration: number, eventName: string) {
        if (duration) {
            const eventData: IViewInjectorClickStreamEventData = {
                event: eventName,
                duration: duration,
            };
            this._logger.debug(
                `Sending elastic notification with the following eventData: ${JSON.stringify(
                    eventData,
                )}`,
            );
            const correlationID = this._utils.getCorrelationId();
            const formatter = this._eventFormatterBuilder.createFormatter(
                '_sendPerformanceNotification',
            );
            const event = formatter.formatUCEvent(
                { message: eventName, durationInMS: duration },
                { correlationID },
                eventData,
            );
            this._trackingProvider.sendEventV2(event);
        }
    }

    private _getFooter = async (
        publicationId: number,
        templateId: number,
        componentId: number,
    ): Promise<string> => {
        const siteSegmentation = await this._siteSegmentationContext.getString();
        return ContentApi.GetFooter(publicationId, templateId, componentId, siteSegmentation);
    };

    private _injectFooter = async () =>
        this._pageContentManager.FooterDO.promise
            .then((footer) => {
                this._logger.debug('Injecting footer...');
                let footerDiv = this._window.document.getElementById(IdsOfElements.UcFooter);
                if (footerDiv) {
                    footerDiv.innerHTML = footer;
                    this._utils.EvaluateScripts(footer);
                } else this._logger.error(`${IdsOfElements.UcFooter} not found`);
            })
            .catch((reason) => {
                this._logger.error(`_injectFooter: Failed to inject the footer, reason: ${reason}`);
            });

    private _toggleCMSContentVisibility(visible: boolean) {
        try {
            const cmsContainer = this._window.document.getElementById(IdsOfElements.UcCMSContainer);
            if (!cmsContainer) {
                this._logger.warn(`${IdsOfElements.UcCMSContainer} does not exist`);
                return;
            }
            if (visible) {
                cmsContainer.classList.remove('transparent-el');
            } else {
                cmsContainer.classList.add('transparent-el');
            }
        } catch (error) {
            this._logger.warn(`${IdsOfElements.UcCMSContainer} does not exist`, error);
        }
    }

    private _updateTitle = () =>
        this._pageContentManager.TitleDO.promise.then(this._updateTitleSync).catch((reason) => {
            throw new Error(reason);
        });

    private _updateTitleSync = (title) => {
        this._logger.debug(`_updateTitleSync: updating title to: ${title}`);
        if (typeof title !== 'undefined') {
            this._window.document.title = title;
        }
    };

    private _updateSeoFooter = () =>
        this._pageContentManager.SeoFooterDO.promise
            .then(this._updateSeoFooterSync)
            .catch((reason) => {
                throw new Error(reason);
            });

    private _updateSeoFooterSync = (seoFooter: string) => {
        this._logger.debug(`_updateSeoFooterSync: updating seoFooter`);
        this._uiChannel.topics.urlData?.publish(
            { publisher: FlowComponents.ViewInjector },
            { seoFooter },
        );
    };

    private _createEmptyComponents = () => {
        this._logger.debug('Creating empty divs...');
        const ucContentContainer = this._window.document.createElement('div');
        ucContentContainer.setAttribute('id', IdsOfElements.UcContentContainer);
        const ucWrapperContainer = this._window.document.createElement('div');
        ucWrapperContainer.setAttribute('id', IdsOfElements.UcWrapperContainer);
        const isClientMode = this._urlUtils.isClientMode();
        if (!isClientMode) ucContentContainer.classList.add('visible--el');
        const ucSpectateSportClientDiv = this._window.document.createElement('div');
        ucSpectateSportClientDiv.setAttribute('id', IdsOfElements.UcSpectateSportClientView);
        const ucFullContentDiv = this._window.document.createElement('div');
        ucFullContentDiv.setAttribute('id', IdsOfElements.UcFullContent);
        const ucFooter = this._window.document.createElement('div');
        ucFooter.setAttribute('id', IdsOfElements.UcFooter);
        const ucCMSContainer = this._window.document.createElement('div');
        ucCMSContainer.setAttribute('id', IdsOfElements.UcCMSContainer);
        ucCMSContainer.append(ucFullContentDiv);
        ucCMSContainer.append(ucFooter);
        this._ucContainer.prepend(ucContentContainer);
        ucContentContainer.prepend(ucCMSContainer);
        ucContentContainer.prepend(ucSpectateSportClientDiv);
        if (!isClientMode) this._ucContainer.append(ucWrapperContainer);
    };

    private _getContentAndTitle = async (uri: string): Promise<IContentAndTitleResult> => {
        const parsedUrl = this._urlUtils.getParsedUrl(uri);
        const siteSegmentation = await this._siteSegmentationContext.getString();
        const { response, errorResponse } = await ContentApi.GetPageContent(
            parsedUrl.pathname,
            this.publicationId,
            siteSegmentation,
        );

        if (errorResponse?.cancelled) {
            return { cancelled: true, title: '', content: '', parsedUrl };
        }

        this.handleContentApiResponse(response);
        const content = response?.content || '';
        const title = response?.title || '';

        return { cancelled: false, title, content, parsedUrl, response, errorResponse };
    };

    private _getContentAndTitleOnClientPage = async (uri: string) => {
        this._logger.debug(`GetContentAndTitleOnClientPage is being called with uri ${uri}`);
        const { cancelled, title, content, parsedUrl } = await this._getContentAndTitle(uri);
        if (cancelled) {
            return;
        }
        const { brandId, subBrandId } = this._window.pageContextManager.getBrandData();
        const { regulationTypeId } = this._window.pageContextManager.getRegulationData();
        const { langIso3 } = this._window.pageContextManager.getLocalizationData();
        let contentResponseTitle = title;

        const { response, errorResponse } = await ContentApi.GetUrlData(
            this._urlUtils.getRelativeURL(parsedUrl.pathname, {
                removeBaseUrl: true,
                removeLanguage: true,
            }),
            brandId,
            subBrandId,
            regulationTypeId,
            langIso3,
        );

        const contentResponseSeoFooter = response?.SEOFooter || '';

        if (!contentResponseTitle) {
            if (errorResponse?.cancelled) {
                return;
            }
            if (response?.Title) {
                contentResponseTitle = response?.Title;
            }

            if (!contentResponseTitle) {
                this._logger.debug('Title is null or undefined');
                const siteSegmentation = await this._siteSegmentationContext.getString();
                let { response, errorResponse } = await ContentApi.GetPageContent(
                    this._urlUtils.getURLWithBase(StaticPaths.Root),
                    this.publicationId,
                    siteSegmentation,
                );

                if (errorResponse?.cancelled) {
                    return;
                }

                this.handleContentApiResponse(response);
                contentResponseTitle = response?.title || '';
            }
        }

        this._pageContentManager.resolveContent(content);
        this._pageContentManager.resolveTitle(contentResponseTitle);
        this._pageContentManager.resolveSeoFooter(contentResponseSeoFooter);
    };

    private _getContentAndTitleOnContentPage = async (uri: string) => {
        this._logger.debug(`GetContentAndTitleOnContentPage is being called with uri ${uri}`);
        const { cancelled, title, content, parsedUrl, response, errorResponse } =
            await this._getContentAndTitle(uri);
        if (cancelled) {
            return;
        }
        let contentResponse = content;
        let contentResponseTitle = title;
        let contentResponseSeoFooter = '';

        if (!response) {
            if (String(errorResponse.status) === NotFound) {
                this._sendNavigationResultEvent(
                    parsedUrl.pathname,
                    false,
                    NavigationResultCode.ContentPageNotFound,
                    NavigationResultDesciption.ContentPageNotFound,
                );

                const siteSegmentation = await this._siteSegmentationContext.getString();
                const { response, errorResponse } = await ContentApi.GetPageContent(
                    StaticPaths.ErrorPage,
                    this.publicationId,
                    siteSegmentation,
                );

                if (errorResponse?.cancelled) {
                    return;
                }

                this.handleContentApiResponse(response);
                contentResponse = response?.content || '';
                contentResponseTitle = response?.title || '';
                contentResponseSeoFooter = response?.seoFooter || '';
            }
        }

        this._pageContentManager.ContentDO.resolve(contentResponse);
        this._pageContentManager.TitleDO.resolve(contentResponseTitle);
        this._pageContentManager.SeoFooterDO.resolve(contentResponseSeoFooter);
    };

    private _updateContent = () =>
        this._pageContentManager.ContentDO.promise.then(this._updateContentSync).catch((reason) => {
            throw new Error(reason);
        });

    private _updateContentSync = (content) => {
        const startTime = Date.now();
        let contentDiv = this._window.document.getElementById(IdsOfElements.UcFullContent);
        contentDiv.innerHTML = content;
        this._utils.EvaluateScripts(content);
        const elapsedTime = Date.now() - startTime;
        this._logger.debug(`_updateContent: Took ${elapsedTime} ms to update content.`);
    };

    private _observeClientMode(): void {
        const isClientMode = this._urlUtils.isClientMode();
        if (!isClientMode) return;
        const cookieContainer = this._b2cIntegration.getCookieContainer();
        this._createHideObserver(cookieContainer);
    }

    private _createHideObserver = (elementId: string) => {
        const bodyObserver = new MutationObserver(() => {
            const htmlElement = this._window.document.getElementById(elementId);
            if (!!htmlElement) {
                bodyObserver.disconnect();
                this._utils.HideElementWithId(elementId);
            }
        });
        bodyObserver.observe(this._window.document.documentElement, {
            childList: true,
        });
    };

    private async _sendNavigationResultEvent(
        url: string,
        isOk: boolean,
        errorCode?: number,
        errorDescription?: string,
    ) {
        const biEventHandler = await this._biEventHandlerPromise;
        biEventHandler?.sendNavigationResultEvent(url, isOk, errorCode, errorDescription);
    }

    private _setFooterContent = async (): Promise<string> => {
        this._logger.debug('SetFooterContent: setting footer content...');
        this._getFooter(this.publicationId, this.templateId, this.componentId).then(
            this._pageContentManager.FooterDO.resolve,
        );

        return this._pageContentManager.FooterDO.promise;
    };

    private _switchActiveViewOnShowContent = async (uri: string) => {
        const cmsContentLoader = await this._cmsContentLoaderPromise;

        this._logger.debug(
            `_switchActiveViewOnShowContent: current view source= ${this.ActiveView}`,
        );
        const nextActiveView = ViewSource.Content;
        const currentActiveView = this.ActiveView;
        this.SetActiveView(nextActiveView);

        this._utils.HideElementWithId(IdsOfElements.UcSpectateSportClientView);
        const isClientMode = this._urlUtils.isClientMode();
        if (!isClientMode) {
            switch (currentActiveView) {
                case ViewSource.FirstHit: {
                    const parsedUrl = this._urlUtils.getParsedUrl(uri);
                    if (this._ucfData?.Is404) {
                        this._sendNavigationResultEvent(
                            parsedUrl.pathname,
                            false,
                            NavigationResultCode.ContentPageNotFound,
                            NavigationResultDesciption.ContentPageNotFound,
                        );
                    }
                    this._injectHiddenContent();
                    if (this._userContext.IsAuthenticated) {
                        await this._updateContentAndFooter();
                    } else {
                        this._injectFooter();
                    }
                    delete this._ucfData?.Is404;
                    break;
                }
                case ViewSource.Content:
                case ViewSource.Sport: {
                    (await this._clientIntegrationFacadePromise).deactivatePublish();
                    this._utils.HideElementWithId(IdsOfElements.UcSpectateSportClientView);
                    this._getContentAndTitleOnContentPage(uri);
                    await this._updateContent();
                    await this._updateTitle();
                    await this._updateSeoFooter();

                    break;
                }
            }
        }

        await cmsContentLoader.ResolveWhenReady(nextActiveView);
    };

    private _switchActiveViewOnShowClient = async (
        uri: string,
        options?: IUrlHandlerOptions,
        resolveWhenReadyCallback?: () => void,
    ) => {
        const nextActiveView = ViewSource.Sport;
        const isClientMode = this._urlUtils.isClientMode();
        if (!isClientMode) {
            const { clientNavigated } = options || {};
            const is404 = this._ucfData?.Is404;
            const currentActiveView = this.ActiveView;
            this.SetActiveView(nextActiveView);
            switch (currentActiveView) {
                case ViewSource.FirstHit: {
                    if (!is404) {
                        this._injectHiddenContent();
                        if (this._userContext.IsAuthenticated) {
                            await this._updateContentAndFooter();
                        } else {
                            this._injectFooter();
                        }
                    } else {
                        delete this._ucfData.is404;
                        this._injectFooter();
                    }
                    break;
                }
                case ViewSource.Content:
                    if (!clientNavigated) {
                        (await this._clientIntegrationFacadePromise).navigatePublish(uri);
                    }
                    this._getContentAndTitleOnClientPage(uri);
                    await this._updateTitle();
                    await this._updateSeoFooter();
                    await this._updateContent();

                    break;
                case ViewSource.Sport: {
                    if (!clientNavigated) {
                        (await this._clientIntegrationFacadePromise).navigatePublish(uri);
                    }

                    this._getContentAndTitleOnClientPage(uri);
                    await this._updateTitle();
                    await this._updateSeoFooter();
                    await this._updateContent();
                    break;
                }
            }
        }
        const cmsContentLoader = await this._cmsContentLoaderPromise;
        cmsContentLoader.ResolveWhenReady(nextActiveView).then(resolveWhenReadyCallback);
    };

    private _updateContentAndFooter = async () => {
        if (this._urlUtils.isClientMode()) return;
        this._setFooterContent();
        const url = this._urlUtils.getRelativeURL(
            this._urlUtils.getURLWithoutHost(this._window.location?.href),
        );
        const relativeUrl = this._urlUtils.getURLWithoutHost(url);
        const isClientNavigation = this._routerUtils.isClientNavigation(relativeUrl);
        if (isClientNavigation) {
            this._getContentAndTitleOnClientPage(relativeUrl);
        } else {
            const appCatalog = await this._appCatalogPromise;

            const isAppNavigationData = await appCatalog.getAppNavigationData(relativeUrl);
            if (!isAppNavigationData) {
                this._getContentAndTitleOnContentPage(relativeUrl);
            } else {
                this._pageContentManager.ContentDO.resolve('');
                this._pageContentManager.TitleDO.resolve('');
            }
        }
        this._injectFooter();
        await this._updateTitle();
        await this._updateContent();
        await this._updateSeoFooter();
    };
}
